// Decisions behind Sentry initialisation. Pure so they can be tested without a browser.

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]', '::1'];

export const isLocalHostname = (hostname: string): boolean => {
   const host = hostname.toLowerCase();
   return LOCAL_HOSTNAMES.includes(host) || host.endsWith('.localhost');
};

// Dev servers and local builds must not report: they come from `nuxt dev`
// (import.meta.dev), a local hostname (any port), or an explicit local APP_ENV.
export const shouldInitSentry = (input: {
   isDev: boolean;
   hostname: string;
   appEnv?: string;
}): boolean => !input.isDev && input.appEnv !== 'local' && !isLocalHostname(input.hostname);

const ENVIRONMENT_BY_API_HOST: Record<string, string> = {
   'api.talktime.ae': 'production',
   'api-stage.talktime.ae': 'staging',
   'api-dev.talktime.ae': 'development',
};

const ENVIRONMENT_BY_APP_HOST: Record<string, string> = {
   'hub.talktime.ae': 'production',
   'hub-stage.talktime.ae': 'staging',
   'hub-dev.talktime.ae': 'development',
};

const hostOf = (url?: string): string => {
   try {
      return url ? new URL(url).hostname.toLowerCase() : '';
   } catch {
      return '';
   }
};

// `nuxt build` forces NODE_ENV=production for every target, so NODE_ENV cannot tell the
// dev deployment from production. APP_ENV is the explicit per-target value; the API and
// page hosts are fallbacks for a build that was made without it.
export const resolveSentryEnvironment = (input: {
   appEnv?: string;
   apiBaseUrl?: string;
   hostname?: string;
   nodeEnv?: string;
}): string =>
   input.appEnv ||
   ENVIRONMENT_BY_API_HOST[hostOf(input.apiBaseUrl)] ||
   ENVIRONMENT_BY_APP_HOST[(input.hostname ?? '').toLowerCase()] ||
   input.nodeEnv ||
   'production';

// Session Replay records the page. The Hub shows health information, so it stays off:
// no sessions and no error-triggered replays in any environment.
export const SENTRY_REPLAY_SAMPLE_RATES = {
   session: 0,
   onError: 0,
} as const;

// Breadcrumbs keep method, url and status, never request or response payloads.
const BODY_KEYS = new Set(['body', 'request_body', 'response_body', 'requestBody', 'responseBody']);

// Query strings and fragments carry email addresses and one-time tokens (for example
// verify-email?email=, delete-confirm?t=&e=), and the activation link carries its token in
// the path. Everything after the path is dropped and that segment is redacted.
export const scrubUrl = (url: string): string =>
   url.replace(/[?#][\s\S]*$/, '').replace(/(\/activate\/)[^/\s]+/, '$1:token');

const URL_DATA_KEYS = ['url', 'from', 'to', 'http.url'];
const URL_DROPPED_KEYS = new Set(['http.query', 'http.fragment']);

const scrubUrlData = (data: Record<string, unknown>): Record<string, unknown> => {
   const result = Object.fromEntries(
      Object.entries(data).filter(([key]) => !URL_DROPPED_KEYS.has(key)),
   );
   for (const key of URL_DATA_KEYS) {
      const value = result[key];
      if (typeof value === 'string') result[key] = scrubUrl(value);
   }
   return result;
};

export const scrubBreadcrumb = <T extends { data?: Record<string, unknown> }>(breadcrumb: T): T => {
   if (!breadcrumb.data) return breadcrumb;
   const data = scrubUrlData(
      Object.fromEntries(Object.entries(breadcrumb.data).filter(([key]) => !BODY_KEYS.has(key))),
   );
   return { ...breadcrumb, data };
};

interface ScrubbableEvent {
   transaction?: string;
   tags?: Record<string, unknown>;
   request?: {
      url?: string;
      headers?: Record<string, string>;
      query_string?: unknown;
   };
   breadcrumbs?: { data?: Record<string, unknown> }[];
   spans?: { op?: string; description?: string; data?: Record<string, unknown> }[];
   contexts?: { trace?: { data?: Record<string, unknown> } };
}

const URL_SPAN_OPS = /^(http|navigation|pageload|resource)/;

// For beforeSend and beforeSendTransaction. The SDK fills request.url from location.href
// and the Referer header from document.referrer, and names transactions after the path.
export const scrubEvent = <T extends ScrubbableEvent>(event: T): T => {
   const next: ScrubbableEvent = { ...event };
   if (typeof next.transaction === 'string') next.transaction = scrubUrl(next.transaction);
   if (next.tags && typeof next.tags.url === 'string') {
      next.tags = { ...next.tags, url: scrubUrl(next.tags.url) };
   }
   if (next.request) {
      const request = { ...next.request };
      if (typeof request.url === 'string') request.url = scrubUrl(request.url);
      delete request.query_string;
      if (request.headers) {
         request.headers = Object.fromEntries(
            Object.entries(request.headers).map(([key, value]) => [
               key,
               /^referer$/i.test(key) && typeof value === 'string' ? scrubUrl(value) : value,
            ]),
         );
      }
      next.request = request;
   }
   if (next.breadcrumbs) next.breadcrumbs = next.breadcrumbs.map((crumb) => scrubBreadcrumb(crumb));
   if (next.spans) {
      next.spans = next.spans.map((span) => ({
         ...span,
         ...(span.data ? { data: scrubUrlData(span.data) } : {}),
         ...(typeof span.description === 'string' && URL_SPAN_OPS.test(span.op ?? '') ?
            { description: scrubUrl(span.description) }
         :  {}),
      }));
   }
   const trace = next.contexts?.trace;
   if (trace?.data) {
      next.contexts = { ...next.contexts, trace: { ...trace, data: scrubUrlData(trace.data) } };
   }
   return next as T;
};

// Everything that keeps personal data out of Sentry events, spread into Sentry.init so the
// settings live in one tested place: no default PII, no component props on Vue errors,
// no session replay, and URLs, request headers and breadcrumbs scrubbed.
export const SENTRY_PRIVACY_OPTIONS = {
   sendDefaultPii: false,
   attachProps: false,
   replaysSessionSampleRate: SENTRY_REPLAY_SAMPLE_RATES.session,
   replaysOnErrorSampleRate: SENTRY_REPLAY_SAMPLE_RATES.onError,
   beforeSend: scrubEvent,
   beforeSendTransaction: scrubEvent,
   beforeBreadcrumb: scrubBreadcrumb,
} as const;

// The Sentry user is the account id and nothing else.
export const toSentryUser = (
   user: { id: string | number } | null | undefined,
): { id: string } | null => (user && user.id != null ? { id: String(user.id) } : null);
